'use client';

import './styles/@theme';

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { SearchField } from '../search-field/search-field.client';
import { useSearchContext } from '../context';
import { getText } from '@/utils/text/text';
import { useStore } from '@/store/store';
import { classes } from '@/utils/classes/classes';

import { GameFactoryClient } from '@/app/components/game/game-factory.client';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { SearchDrawerSkeleton } from '@/app/components/search-drawer/subcomponents/skeleton/skeleton';
import { parseSearchGames } from '@/logic/games/parse-search-games';
import { COOKIES } from '@/utils/cookies';
import { getCookieValue } from '@/utils/cookies/client';
import {
    trackSearchPageViewed,
    trackSearchResultsReturned,
    trackSearchResultSelected
} from '@/tracking/amplitude/events/general/search-events';
import { SEARCH } from '@/utils/constants';
import { ddRumAction, ddStopVital, ddCancelVital } from '@/utils/datadog';
import { SEARCH_VITALS } from '@/utils/datadog/vitals';

import { ensureCoreEventsInitialized } from '@/hooks/use-core-events';
import {
    EVENT_TYPE,
    getGameElementData,
    getGameElementDataFromZoneGame,
    getSnowplowEvent,
    SNOWPLOW_CONTEXT_MAPPING,
    type GameZoneSnowplowConfig
} from '@/tracking/snowplow/utils';
import { getGameName } from '@/logic/game-adapter/game-adapter';

import type { Region } from '@/types/constants';
import { useGlobalContext } from '@/store/contexts/global-context';
import type { Game as GameType, GameTrackingInfo } from '@/types/games';
import searchDrawerEmptyState from '@/assets/images/search-drawer-empty-state.svg';
import { AMPLITUDE_EXPERIMENTS } from '@/utils/experiments';
import { sendExposureEvent } from '@/tracking/amplitude/events/general/experiment-exposure';

const SEARCH_DRAWER_SCROLLABLE_ID = 'search-drawer-scrollable';

const SEARCH_RESULTS_SNOWPLOW_ZONE: GameZoneSnowplowConfig = {
    zoneName: 'search_results',
    zoneType: 'game_zone',
    zonePosition: 1,
    zoneLayout: null
};

const gameToTrackingInfo = (game: GameType): GameTrackingInfo => ({
    uid: game.uid,
    rank_order: (game as GameType & { rank_order?: number | string | null }).rank_order ?? null,
    data: {
        ganid: game.data.ganid,
        display_name: getGameName(game.data.display_name),
        internal_code: game.data.internal_code,
        trigger_game_name: game.data.trigger_game_name,
        mean_sps_rank: game.data.mean_sps_rank,
        mean_sps: game.data.mean_sps,
        model_type: (game.data as typeof game.data & { model_type?: string }).model_type
    }
});

type DrawerLayoutMetrics = {
    topPosition: number;
    heightPx: number;
};

const getHeaderLayoutElements = (): {
    header: HTMLElement;
    headerContent: HTMLElement;
    headerFixed: HTMLElement;
} | null => {
    const [header] = document.getElementsByTagName('header');
    if (!header) {
        return null;
    }
    const [headerContent] = header.getElementsByClassName('header-content');
    const [headerFixed] = header.getElementsByClassName('header-fixed');
    return {
        header: header as HTMLElement,
        headerContent: headerContent as HTMLElement,
        headerFixed: headerFixed as HTMLElement
    };
};

const getSearchRedesignDrawerLayout = (
    scrollTop: number,
    header: HTMLElement,
    headerContent: HTMLElement,
    headerFixed: HTMLElement
): DrawerLayoutMetrics => {
    const fixedHeight = headerFixed.clientHeight;
    const headerHeight = header.clientHeight;

    if (scrollTop === 0) {
        return { topPosition: 0 - fixedHeight, heightPx: headerHeight - fixedHeight };
    }
    if (scrollTop <= headerContent.clientHeight) {
        return { topPosition: 0 - fixedHeight, heightPx: headerHeight - scrollTop };
    }
    return { topPosition: 0 - fixedHeight, heightPx: fixedHeight };
};

const getOldSearchDrawerLayout = (
    scrollTop: number,
    header: HTMLElement,
    headerContent: HTMLElement,
    headerFixed: HTMLElement
): DrawerLayoutMetrics => {
    const fixedHeight = headerFixed.clientHeight;
    const headerHeight = header.clientHeight;

    if (scrollTop === 0) {
        return { topPosition: headerHeight, heightPx: headerHeight };
    }
    if (scrollTop <= headerContent.clientHeight) {
        return { topPosition: headerHeight, heightPx: headerHeight - scrollTop };
    }
    return { topPosition: fixedHeight + scrollTop, heightPx: fixedHeight };
};

type SearchDrawerProps = {
    children: React.ReactNode;
};

const SearchDrawer: React.FC<SearchDrawerProps> = ({ children }) => {
    const [topPosition, setTopPosition] = useState(0);
    const [drawerHeight, setDrawerHeight] = useState('');
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
    const prevIsSearchOpenRef = useRef<boolean | null>(null);

    const region = getCookieValue(COOKIES.REGION) as Region;

    const {
        initializeGames,
        foundGames,
        query,
        clearQuery,
        hasMore,
        isLoadingMore,
        fetchNextPage,
        allGames,
        isLoading,
        searchEngineVersion
    } = useSearchContext();
    const search = useStore((s) => s.search);
    const { isMobile, isLoggedIn, experiments } = useGlobalContext();
    const isSearchRedesignOn = useStore((s) => s.search.isSearchRedesignVariant);
    const isGlobalLoading = useStore((s) => s.loading.isLoading);
    const { sendEvent } = useStore((state) => state.tracking);

    const handleCloseSearch = () => {
        clearQuery();
        search.toggleIsSearchOpen();
    };

    const updateDrawerPositionAndHeight = (): void => {
        const elements = getHeaderLayoutElements();
        if (!elements) {
            return;
        }

        const { header, headerContent, headerFixed } = elements;
        const { scrollTop } = document.body;

        if (isSearchRedesignOn) {
            const redesignLayout = getSearchRedesignDrawerLayout(scrollTop, header, headerContent, headerFixed);
            setTopPosition(redesignLayout.topPosition);
            setDrawerHeight(`calc(100vh - ${redesignLayout.heightPx}px)`);
            return;
        }

        if (isMobile) {
            return;
        }

        const oldLayout = getOldSearchDrawerLayout(scrollTop, header, headerContent, headerFixed);
        setTopPosition(oldLayout.topPosition);
        setDrawerHeight(`calc(100vh - ${oldLayout.heightPx}px)`);
    };

    useEffect(() => {
        if (search.isSearchOpen && allGames.length === 0) {
            initializeGames();
        }

        updateDrawerPositionAndHeight();
    }, [search.isSearchOpen, allGames.length, initializeGames]);

    useEffect(() => {
        if (prevIsSearchOpenRef.current !== null && prevIsSearchOpenRef.current !== search.isSearchOpen) {
            ddRumAction({
                name: search.isSearchOpen ? 'SEARCH_DRAWER_OPENED' : 'SEARCH_DRAWER_CLOSED',
                description: search.isSearchOpen ? 'Search drawer opened' : 'Search drawer closed',
                use_case: 'GLOBAL',
                context: { isSearchOpen: search.isSearchOpen }
            });

            if (search.isSearchOpen) {
                sendEvent([{ destination: 'amplitude', eventProps: trackSearchPageViewed() }]);
                sendExposureEvent(experiments, AMPLITUDE_EXPERIMENTS.SEARCH_ML, sendEvent);
            }
        }
        if (prevIsSearchOpenRef.current !== null && !prevIsSearchOpenRef.current && search.isSearchOpen) {
            const topNavPillActive = document.querySelector('nav li.active span.image-text__span')?.textContent ?? '';
            void (async () => {
                try {
                    await ensureCoreEventsInitialized();
                    const snowplowPayload = getSnowplowEvent(EVENT_TYPE.SEARCH_OPENED, {
                        eventCategory: 'search',
                        topNavPillActive
                    });
                    sendEvent([{ destination: 'snowplow', eventProps: snowplowPayload }]);
                } catch {
                    // Logged in ensureCoreEventsInitialized
                }
            })();
        }
        prevIsSearchOpenRef.current = search.isSearchOpen;
    }, [search.isSearchOpen, sendEvent, experiments]);

    useEffect(() => {
        if (!search.isSearchOpen) {
            ddStopVital(SEARCH_VITALS.QUERY_RESPONSE, {
                context: { cancelled: true, cancel_reason: 'drawer_closed' }
            });
            ddCancelVital(SEARCH_VITALS.RECOMMENDED_READY);
            clearQuery();
        }
    }, [search.isSearchOpen, clearQuery]);

    useEffect(() => {
        if (isGlobalLoading) {
            ddStopVital(SEARCH_VITALS.QUERY_RESPONSE, {
                context: { cancelled: true, cancel_reason: 'navigation' }
            });
            ddCancelVital(SEARCH_VITALS.RECOMMENDED_READY);
        }
    }, [isGlobalLoading]);

    const hasReportedInitialFoundGamesRef = useRef(false);
    useEffect(() => {
        if (!hasReportedInitialFoundGamesRef.current) {
            hasReportedInitialFoundGamesRef.current = true;
            return;
        }
        ddStopVital(SEARCH_VITALS.QUERY_RESPONSE, {
            context: { results_count: foundGames.length }
        });
    }, [foundGames]);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const contentWrapperRef = useRef<HTMLDivElement>(null);
    const foundGamesRef = useRef(foundGames);
    const searchEngineVersionRef = useRef(searchEngineVersion);
    const lastFiredSearchQueryRef = useRef<string | null>(null);
    useEffect(() => {
        // foundGamesRef is used by the trackSearchResultSelected function which is
        // inside a useCallback, so we need to update it when foundGames changes.
        foundGamesRef.current = foundGames;
        searchEngineVersionRef.current = searchEngineVersion;
    }, [foundGames.length, searchEngineVersion]);

    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const scrollContainer = scrollContainerRef.current;
        if (!sentinel || !scrollContainer || !hasMore || isLoadingMore) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry?.isIntersecting) {
                    fetchNextPage();
                }
            },
            { root: scrollContainer, rootMargin: '100px', threshold: 0 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, isLoadingMore, fetchNextPage, foundGames.length]);

    useEffect(() => {
        if (!query || query.length < SEARCH.MIN_QUERY_LENGTH) {
            lastFiredSearchQueryRef.current = null;
            return;
        }

        // Fire only once per unique query — results may arrive in multiple batches.
        if (lastFiredSearchQueryRef.current === query) {
            return;
        }

        lastFiredSearchQueryRef.current = query;

        const games = foundGamesRef.current;
        const resultCount = games.length;

        sendEvent([{ destination: 'amplitude', eventProps: trackSearchResultsReturned(query, resultCount) }]);

        void (async () => {
            try {
                await ensureCoreEventsInitialized();
                const latestGames = foundGamesRef.current;
                const contexts = latestGames.slice(0, 10).map((game, i) => {
                    const el = document?.querySelector(`.found-games_container [data-gameuid="${game.uid}"]`);
                    const onScreen = el
                        ? el.getBoundingClientRect().top < window.innerHeight && el.getBoundingClientRect().bottom > 0
                        : false;
                    return {
                        contextType: SNOWPLOW_CONTEXT_MAPPING.GAME_IMPRESSION,
                        contextProps: getGameElementDataFromZoneGame(
                            SEARCH_RESULTS_SNOWPLOW_ZONE,
                            i + 1,
                            onScreen,
                            gameToTrackingInfo(game)
                        )
                    };
                });
                if (contexts.length === 0) {
                    contexts.push({
                        contextType: SNOWPLOW_CONTEXT_MAPPING.GAME_IMPRESSION,
                        contextProps: getGameElementData({
                            zoneName: SEARCH_RESULTS_SNOWPLOW_ZONE.zoneName,
                            zonePosition: SEARCH_RESULTS_SNOWPLOW_ZONE.zonePosition,
                            zoneType: SEARCH_RESULTS_SNOWPLOW_ZONE.zoneType,
                            position: 0,
                            gameId: '',
                            gameName: '',
                            onScreen: false
                        })
                    });
                }
                const snowplowPayload = getSnowplowEvent(
                    EVENT_TYPE.SEARCH_RESULTS_RETURNED,
                    {
                        eventCategory: 'search',
                        searchKeyword: query.toLowerCase(),
                        totalSearchResults: latestGames.length,
                        searchMlModelVersion: searchEngineVersion
                    },
                    contexts
                );
                sendEvent([{ destination: 'snowplow', eventProps: snowplowPayload }]);
            } catch {
                // Logged in ensureCoreEventsInitialized; Amplitude already sent above.
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendEvent, foundGames.length]);

    const translations = {
        header: getText({
            text: isSearchRedesignOn ? 'components.search.searchRedesignResultFor' : 'components.search.searchResultFor'
        }),
        nothingTurnedUp: getText({ text: 'components.search.searchRedesignNothingTurnedUp' }),
        notFound: getText({ text: 'components.search.searchNotFoundText' }),
        notFoundRedesign: getText({ text: 'components.search.searchRedesignNotFoundText' }),
        cancelButton: getText({ text: 'components.search.searchCancelText' })
    };

    const hasValidQuery = Boolean(query && query.length >= SEARCH.MIN_QUERY_LENGTH);
    const showSkeleton = isLoading && hasValidQuery;
    const showNoResults = !isLoading && hasValidQuery && foundGames.length === 0;
    const showResults = !isLoading && hasValidQuery && foundGames.length > 0;

    useEffect(() => {
        if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
        if (contentWrapperRef.current) contentWrapperRef.current.scrollTop = 0;
    }, [query]);

    const searchGames = useMemo(
        () => (showResults ? parseSearchGames(foundGames, { region, isMobile }).games : []),
        [showResults, foundGames, region, isMobile]
    );

    const triggerTrackEvent = useCallback(
        (gameId?: string): void => {
            const isSearchResultClick =
                search.isSearchOpen && gameId && query && query.length >= SEARCH.MIN_QUERY_LENGTH;
            if (!isSearchResultClick) {
                return;
            }

            const games = foundGamesRef.current;
            const resultCount = games.length;
            sendEvent([
                { destination: 'amplitude', eventProps: trackSearchResultSelected(query, resultCount, gameId) }
            ]);

            const clickedGame = games.find((g) => g.uid === gameId);
            if (!clickedGame) {
                return;
            }
            const slotIndex = games.findIndex((g) => g.uid === gameId);
            const position1Based = slotIndex >= 0 ? slotIndex + 1 : 1;

            void (async () => {
                try {
                    await ensureCoreEventsInitialized();
                    const gameElement = getGameElementDataFromZoneGame(
                        SEARCH_RESULTS_SNOWPLOW_ZONE,
                        position1Based,
                        true,
                        gameToTrackingInfo(clickedGame)
                    );
                    const snowplowPayload = getSnowplowEvent(
                        EVENT_TYPE.SEARCH_RESULTS_SELECTED,
                        {
                            position: position1Based,
                            eventCategory: 'search',
                            searchKeyword: query.toLowerCase(),
                            totalSearchResults: resultCount,
                            searchMlModelVersion: searchEngineVersionRef.current
                        },
                        [
                            {
                                contextType: SNOWPLOW_CONTEXT_MAPPING.GAME_IMPRESSION,
                                contextProps: gameElement
                            }
                        ]
                    );
                    sendEvent([{ destination: 'snowplow', eventProps: snowplowPayload }]);
                } catch {
                    // Logged in ensureCoreEventsInitialized; Amplitude already sent above.
                }
            })();
        },
        [search.isSearchOpen, query, sendEvent]
    );

    return (
        <div
            ref={scrollContainerRef}
            data-testid="search-drawer"
            id={SEARCH_DRAWER_SCROLLABLE_ID}
            className={classes(
                'search-drawer',
                isMobile && 'mobile',
                isSearchRedesignOn && 'search-drawer--experiment-v2'
            )}
            style={{ top: topPosition, height: drawerHeight }}>
            <div className="field-container">
                <SearchField />
                <button data-testid="cancel-button" className="cancel" onClick={handleCloseSearch}>
                    {translations.cancelButton}
                </button>
            </div>
            <div
                ref={contentWrapperRef}
                className="search-drawer-content-wrapper"
                style={isSearchRedesignOn ? { height: drawerHeight } : {}}>
                <div className="search-drawer-content">
                    {showSkeleton && (
                        <div className="found-games" data-testid="search-drawer-skeleton">
                            <p className="header">
                                {isSearchRedesignOn ? translations.header : `${translations.header} "${query}"`}
                            </p>
                            <div className="found-games_container">
                                <SearchDrawerSkeleton />
                            </div>
                        </div>
                    )}
                    {showNoResults && (
                        <>
                            {isSearchRedesignOn ? (
                                <div className="search-drawer-empty-state" role="status">
                                    <img
                                        src={searchDrawerEmptyState.src}
                                        alt=""
                                        aria-hidden="true"
                                        className="search-drawer-empty-state__image"
                                    />
                                    <p className="search-drawer-empty-state__title">{translations.nothingTurnedUp}</p>
                                    <p className="search-drawer-empty-state__hint">{translations.notFoundRedesign}</p>
                                </div>
                            ) : (
                                <p className="header">{translations.notFound}</p>
                            )}
                        </>
                    )}
                    {showResults && (
                        <div className="found-games" data-t-module="search_results" data-t-zonename="search_results">
                            <p className="header">
                                {isSearchRedesignOn ? translations.header : `${translations.header} "${query}"`}
                            </p>
                            <div className="found-games_container">
                                {searchGames.map((game, index) => (
                                    <GameFactoryClient
                                        key={game.uid}
                                        game={game}
                                        triggerTrackEvent={triggerTrackEvent}
                                        tracking={{ gamePosition: index + 1 }}
                                        hideGameInfo={isSearchRedesignOn && isLoggedIn}
                                    />
                                ))}
                                {hasMore && (
                                    <div
                                        ref={loadMoreSentinelRef}
                                        className="search-drawer-load-more-sentinel"
                                        aria-hidden="true"
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    {!showSkeleton && foundGames.length < 4 && <>{children}</>}
                </div>
            </div>
        </div>
    );
};

export default SearchDrawer;
